import type { UpdateEnterpriseRiskMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateEnterpriseRiskDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { UpdateEnterpriseRiskMutationVariables } from '../nativeMutationInputs';
import type { FrontendRouterInputs } from '../routerInputs';

type UpdateEnterpriseRiskTRPCInput =
  FrontendRouterInputs['enterpriseRisk']['update'];

export const useUpdateEnterpriseRisk = createMutationHook({
  name: 'updateEnterpriseRisk',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.enterpriseRisk.update.mutationOptions({ onSuccess }),
  mapInput: (
    variables: UpdateEnterpriseRiskMutationVariables
  ): UpdateEnterpriseRiskTRPCInput => {
    const base = {
      Id: variables.Id,
      OriginalTimestamp: variables.OriginalTimestamp,
      Title: variables.Title,
      Description: variables.Description ?? '',
      Treatment: variables.Treatment ?? null,
      CustomAttributeData: variables.CustomAttributeData ?? null,
    };

    if (variables.Tier === 1) {
      return { ...base, Tier: 1, ParentId: null };
    }

    if (!variables.ParentId) {
      throw new Error(
        'A parent enterprise risk is required for tier 2 and tier 3 risks'
      );
    }

    if (variables.Tier === 2) {
      return { ...base, Tier: 2, ParentId: variables.ParentId };
    }

    if (variables.Tier === 3) {
      return { ...base, Tier: 3, ParentId: variables.ParentId };
    }

    throw new Error(`Unsupported enterprise risk tier: ${variables.Tier}`);
  },
  invalidate: (trpc) => [
    trpc.frontend.enterpriseRisk.register.queryKey(),
    trpc.frontend.enterpriseRisk.getById.queryKey(),
    trpc.frontend.enterpriseRisk.getByTier.queryKey(),
  ],
  // The tRPC update returns the full row; the V2 GraphQL mutation reported a
  // single affected row, so mirror that shape.
  mapResponse: (): UpdateEnterpriseRiskMutation => ({
    updateChildEnterpriseRisk: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateEnterpriseRiskDocument,
  graphqlVariables: ({
    OriginalTimestamp: _token,
    ...variables
  }: UpdateEnterpriseRiskMutationVariables) => ({
    ...variables,
  }),
  evictFields: ['enterprise_risk'],
  refetchQueries: [
    namedOperations.Query.getEnterpriseRiskById,
    namedOperations.Query.getEnterpriseRisks,
  ],
});
